import { useSnackbar } from '@components/Snackbar';
import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';

export interface ActionHook<T> {
  pending: boolean;
  error: string | null;
  data: T | null;
  dataMapView?: T | null;
  start: (
    options?: ActionHookOptions<T>,
  ) => { data: T; dataMapView?: T } | Promise<{ data: T; dataMapView?: T }>;
  reset: () => void;
}

interface ActionHookOptions<T> {
  action: () => { data: T; dataMapView?: T } | Promise<{ data: T; dataMapView?: T }>;
  errorMessage?: string;
  successMessage?: string;
  showErrorToast?: boolean;
  showSuccessToast?: boolean;
}

type InitialOptions<T = void> = ActionHookOptions<T> & { manual: boolean };

export const useAction = <T = void>(initialOptions?: InitialOptions<T>): ActionHook<T> => {
  const [data, setData] = useState<T | null>(null);
  const [dataMapView, setDataMapView] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const { showSnackbar } = useSnackbar();

  const start = useCallback(
    async (options?: ActionHookOptions<T>): Promise<{ data: T; dataMapView?: T }> => {
      if (initialOptions && options) {
        throw new Error(
          'You cannot call start() with options if you have already passed options to useAction()',
        );
      }

      const {
        action,
        errorMessage,
        successMessage,
        showSuccessToast = true,
        showErrorToast = true,
      } = initialOptions ?? options ?? {};
      if (!action) {
        throw new Error('You must provide an action to useAction()');
      }

      setPending(true);
      setError(null);
      try {
        const { data, dataMapView } = await action();
        setData(data);
        if (dataMapView) {
          setDataMapView(dataMapView);
        }

        if (showSuccessToast && successMessage) {
          showSnackbar(successMessage, 'success');
        }

        return { data, dataMapView };
      } catch (error) {
        console.error(error);
        let message = errorMessage ?? '';
        if (axios.isAxiosError(error)) {
          if (error.response?.data.message) {
            message += ` ${error.response.data.message}`;
          }
        } else if (error instanceof Error && error.message) {
          message += ` ${error.message}`;
        }

        if (!message) {
          message = 'An error occurred.';
        }

        setError(message);

        if (showErrorToast) {
          showSnackbar(message, 'error');
        }

        // Only throw errors if they aren't in the 400 range
        if (
          !axios.isAxiosError(error) ||
          (error.response?.status && error.response.status < 400 && error.response.status >= 500)
        ) {
          throw Error(message);
        } else {
          return Promise.reject(message);
        }
      } finally {
        setPending(false);
      }
    },
    [initialOptions],
  );

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setPending(false);
  }, []);

  useEffect(() => {
    if (initialOptions && !initialOptions.manual) {
      void start();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on mount
  }, []);

  return { pending, error, data, dataMapView, start, reset };
};
